import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Eye, Trash2, Image as ImageIcon, History, Globe } from 'lucide-react';
import { TempleRegistration } from './types';
import { getDisplayStatus, getStatusBadgeColor } from './helpers';
import { PaginationState } from '@tanstack/react-table';

interface GetColumnsProps {
    activeTab: string;
    pagination: PaginationState;
    handleViewDetails: (reg: TempleRegistration) => void;
    handleDelete: (reg: TempleRegistration) => void;
    onManageMedia?: (reg: TempleRegistration) => void;
    onManageDomain?: (reg: TempleRegistration) => void;
    onTogglePopular?: (templeId: string, isPopular: boolean) => void;
    onViewHistory?: (reg: TempleRegistration) => void;
}

export const getColumns = ({
    activeTab,
    pagination,
    handleViewDetails,
    handleDelete,
    onManageMedia,
    onManageDomain,
    onTogglePopular,
    onViewHistory
}: GetColumnsProps): ColumnDef<TempleRegistration>[] => {
    const cols: ColumnDef<TempleRegistration>[] = [
        {
            id: 'serialNo',
            header: 'S No.',
            cell: info => (pagination.pageIndex * pagination.pageSize) + info.row.index + 1,
            enableSorting: false,
        },
        {
            id: 'temple.name',
            accessorKey: 'temple.name',
            header: 'Temple Name',
            cell: info => (
                <div>
                    <div className="font-medium text-gray-900">{info.getValue() as string}</div>
                    {activeTab !== 'existing' && <div className="text-xs text-gray-500">{info.row.original.temple.address?.city}</div>}
                </div>
            )
        },
        // temple admin name
        {
            id: 'user.name',
            accessorKey: 'user.name',
            header: 'Admin Name',
            cell: info => info.getValue() as string
        },
        // temple admin email
        {
            id: 'user.email',
            accessorKey: 'user.email',
            header: 'Admin Email',
            cell: info => info.getValue() as string
        }
    ];

    if (activeTab === 'new') {
        cols.push({
            id: 'status',
            header: 'Status',
            cell: info => {
                const displayStatus = getDisplayStatus(info.row.original);
                return (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(displayStatus)}`}>
                        {displayStatus}
                    </span>
                );
            }
        });
        cols.push({
            id: 'createdAt',
            accessorKey: 'createdAt',
            header: 'Received Date',
            cell: info => format(new Date(info.getValue() as string), 'MMM dd, yyyy')
        });
        cols.push({
            id: 'callScheduledAt',
            accessorKey: 'callScheduledAt',
            header: 'Scheduled Call',
            cell: info => info.getValue() ? (
                <span className="text-orange-600 font-medium">
                    {format(new Date(info.getValue() as string), 'MMM dd, hh:mm a')}
                </span>
            ) : <span className="text-gray-400 text-xs px-2 py-1 bg-gray-50 rounded border text-center">Not Scheduled</span>
        });
    }

    if (activeTab === 'under_review' || activeTab === 'setup_pending') {
        cols.push({
            id: 'createdAt',
            accessorKey: 'createdAt',
            header: 'Received Date',
            cell: info => format(new Date(info.getValue() as string), 'MMM dd, yyyy')
        });
        cols.push({
            accessorKey: 'assignedTo',
            header: 'Assigned To',
            cell: info => {
                const assigned = info.getValue() as TempleRegistration['assignedTo'];
                return assigned ? (
                    <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                        {assigned.name}
                    </span>
                ) : <span className="text-gray-400 italic text-xs">Unassigned</span>;
            }
        });
    }

    if (activeTab === 'under_review') {
        cols.push({
            id: 'verificationStatus',
            header: 'Status',
            enableSorting: false,
            cell: info => {
                const displayStatus = getDisplayStatus(info.row.original);
                return (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(displayStatus)}`}>
                        {displayStatus}
                    </span>
                );
            }
        });
    }

    if (activeTab === 'setup_pending') {
        cols.push(
            {
                accessorKey: 'callScheduledAt',
                header: 'Call Scheduled Time',
                cell: info => info.getValue() ? format(new Date(info.getValue() as string), 'MMM dd, hh:mm a') : 'Not Scheduled'
            },
            {
                accessorKey: 'setupStatus',
                header: 'Status',
                cell: info => {
                    const displayStatus = getDisplayStatus(info.row.original);
                    return (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(displayStatus)}`}>
                            {displayStatus}
                        </span>
                    );
                }
            }
        );
    }

    if (activeTab === 'existing') {
        cols.push(
            {
                id: 'plan',
                header: 'Setup Plan',
                enableSorting: false,
                cell: () => <span className="text-gray-500">Standard Plan</span>
            },
            {
                accessorKey: 'temple.contact.phone',
                header: 'Phone No',
            },
            // {
            //     id: 'subStart',
            //     header: 'Sub. Start Date',
            //     enableSorting: false,
            //     cell: ({ row }) => {
            //         const temple = row.original.createdTemple;
            //         if (typeof temple === 'string' || !temple?.subscriptionStartDate) return '-';
            //         return new Date(temple.subscriptionStartDate).toLocaleDateString();
            //     }
            // },
            {
                id: 'subEnd',
                header: 'Sub. End Date',
                enableSorting: false,
                cell: ({ row }) => {
                    const temple = row.original.createdTemple;
                    if (typeof temple === 'string' || !temple?.subscriptionEndDate) return '-';
                    return new Date(temple.subscriptionEndDate).toLocaleDateString();
                }
            }
        );
    }

    if (activeTab === 'existing' && onTogglePopular) {
        cols.push({
            id: 'isPopular',
            header: 'Popular',
            enableSorting: false,
            cell: ({ row }) => {
                const temple = row.original.createdTemple;
                // Check if temple is an object (populated) and has an ID
                if (typeof temple === 'string' || !temple) return null;

                return (
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={!!temple.isPopular}
                            onChange={(e) => onTogglePopular(temple._id, e.target.checked)}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    </label>
                );
            }
        });
    }

    // Actions Column
    cols.push({
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: info => (
            <div className="flex items-center gap-2">
                <button
                    onClick={(e) => { e.stopPropagation(); handleViewDetails(info.row.original); }}
                    className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                    title="View Details"
                >
                    <Eye size={18} />
                </button>
                {activeTab === 'existing' && onManageMedia && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onManageMedia(info.row.original); }}
                        className="p-1.5 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors duration-200"
                        title="Manage Media"
                    >
                        <ImageIcon size={18} />
                    </button>
                )}
                {activeTab === 'existing' && onManageDomain && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onManageDomain(info.row.original); }}
                        className="p-1.5 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded-lg transition-colors duration-200"
                        title="Manage Custom Domain"
                    >
                        <Globe size={18} />
                    </button>
                )}
                {onViewHistory && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onViewHistory(info.row.original); }}
                        className="p-1.5 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded-lg transition-colors duration-200"
                        title="View History"
                    >
                        <History size={18} />
                    </button>
                )}
                <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(info.row.original); }}
                    className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors duration-200"
                    title="Delete Registration"
                >
                    <Trash2 size={18} />
                </button>
            </div>
        )
    });

    return cols;
};
